import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ErrorReference } from '../error/error-reference'

// The workspace's own confirmation (LeaveGuard's pattern, never
// window.confirm): modal, Escape is Cancel, and focus starts on Cancel, the
// safe choice. Portalled to <body> so it sits over the pinned header and
// outside the page's <form>. `error` keeps it open with the server's message;
// `failure`, the error behind it, adds its reference (error-reference.js).
// `children` sit under the body (a reason field); `confirmDisabled` holds
// the confirm button until they are filled in; `confirmClassName` is the
// button's look (a destructive red by default). `scrim` dims the page behind
// properly, in both themes (.sw-scrim, sso-workspace.scss), for a dialog that
// must hold the eye (the API key actions, owner 2026-10-29); without it the
// backdrop is the drawers' light one, as everywhere else.
export const ConfirmDialog = ({ open, title, body, confirmText, busy, error, failure, onConfirm, onCancel, children, confirmDisabled, confirmClassName = 'btn sw-btn-danger', scrim = false }) => {
  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  return createPortal(
    <div className={`sw-drawer${scrim ? ' sw-scrim' : ''} justify-content-center align-items-center`} data-testid="confirm-backdrop" onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="sw-dialog" role="alertdialog" aria-modal="true" aria-labelledby="sw-confirm-title" aria-describedby="sw-confirm-body">
        <h2 className="h5" id="sw-confirm-title">{title}</h2>
        <p className="sw-hint" id="sw-confirm-body">{body}</p>
        {children}
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} className="mb-3" />}
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" autoFocus onClick={onCancel}>Cancel</button>
          <button type="button" className={confirmClassName} disabled={busy || !!confirmDisabled} onClick={onConfirm}>{confirmText}</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
